import {
  BanIcon,
  Button,
  Card,
  CheckIcon,
  DeleteIcon,
  EditIcon,
  ImportantIcon,
  Input,
  Modal,
} from "@bp/ui-components";
import { useState } from "react";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { useConfirm } from "../../../hooks/useConfirm";
import {
  DIAG_SUMMARY,
  type DiagnosableAccount,
  type DiagStatus,
  diagnose,
  diagUserFromAccount,
} from "../../../utils/diagnose";
import { fmtDateTime, languageLabel } from "../../../utils/format";
import { INVITATION_OTP_TTL_MS, RESET_PASSWORD_OTP_TTL_MS } from "../../../utils/otpWindow";
import { productIdFromUrl, productLabel } from "../../../utils/product";
import { sendPasswordResetLink } from "../../../utils/passwordReset";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import { DetailRow } from "../../DetailRow/DetailRow";
import { StatusBadge } from "../../StatusBadge/StatusBadge";
import { PendingLinkValue } from "../PendingLinkRow/PendingLinkRow";
import { ResendInvitationModal } from "../ResendInvitationModal/ResendInvitationModal";
import { type AccountAction, type AccountRow, accountActions } from "./accountActions";
import styles from "./AccountCard.module.scss";

const AccountCard_UpdatePassword = graphql(`
  mutation AccountCard_UpdatePassword($userUuid: ID!, $newPassword: String!, $oldPassword: String) {
    updatePasswords(
      where: { uuid: $userUuid }
      update: { oldPassword: $oldPassword, newPassword: $newPassword }
    ) {
      users {
        uuid
      }
    }
  }
`);

const AccountCard_UpdateUser = graphql(`
  mutation AccountCard_UpdateUser($where: UserWhere, $update: UserUpdateInput) {
    updateUsers(where: $where, update: $update) {
      users {
        uuid
      }
    }
  }
`);

const AccountCard_RevokeInvitation = graphql(`
  mutation AccountCard_RevokeInvitation($userUuid: ID!) {
    revokeInvitation(userUuid: $userUuid) {
      users {
        uuid
      }
    }
  }
`);

const AccountCard_RevokePasswordReset = graphql(`
  mutation AccountCard_RevokePasswordReset($userUuid: ID!) {
    revokePasswordReset(userUuid: $userUuid) {
      users {
        uuid
      }
    }
  }
`);

const AccountCard_DeleteUser = graphql(`
  mutation AccountCard_DeleteUser($where: UserWhere) {
    deleteUsers(where: $where) {
      __typename
    }
  }
`);

export type AccountCardUser = Omit<DiagnosableAccount, "twoFactorMethods"> & {
  newEmail?: string | null;
  invitationFrontendUrl?: string | null;
  resetPasswordOtpValidUntil?: string | null;
  recoveryCodesGeneratedAt?: string | null;
  preferredLanguage?: string | null;
  twoFactorMethods?: ReadonlyArray<{ uuid: string }> | null;
};

type Props = {
  user: AccountCardUser;
  /** Active profile — the invitation is sent per profile, not per account. */
  profileUuid?: string;
  /** Profiles linked to the account — they survive the account deletion. */
  profileCount: number;
  /** Invitation products the active profile can reach (drives the product choice). */
  invitationProducts?: readonly ("bp" | "planung")[];
  /** URL of each product's application, sent with the invitation as its frontend URL. */
  invitationProductUrls?: Partial<Record<"bp" | "planung", string>>;
  onEdit: () => void;
  /** Re-run the page query after an account write. */
  onChanged: () => void;
  onDeleted: () => void;
};

type IconComp = typeof CheckIcon;
const BANNER_ICON: Record<DiagStatus, IconComp> = {
  ok: CheckIcon,
  warn: ImportantIcon,
  err: BanIcon,
};
const BANNER_CLASS: Record<DiagStatus, string> = {
  ok: styles.bannerOk,
  warn: styles.bannerWarn,
  err: styles.bannerErr,
};

/**
 * The login account: its state row by row, and every support action on the
 * row whose state it changes (one home per action). The login diagnosis shows
 * up as a banner when a check fails; the header carries it as badges. Deletion is
 * the one head button next to "Bearbeiten". Password, account state and deletion go through
 * the PIM backend; re-sending the invitation calls the auth-server.
 */
export function AccountCard({
  user,
  profileUuid,
  profileCount,
  invitationProducts = [],
  invitationProductUrls,
  onEdit,
  onChanged,
  onDeleted,
}: Props) {
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [, updatePassword] = useMutation(AccountCard_UpdatePassword);
  const [, updateUser] = useMutation(AccountCard_UpdateUser);
  const [, deleteUser] = useMutation(AccountCard_DeleteUser);
  const [, revokeInvitation] = useMutation(AccountCard_RevokeInvitation);
  const [, revokePasswordReset] = useMutation(AccountCard_RevokePasswordReset);
  const { confirm, ConfirmationDialog } = useConfirm();

  const title = user.email || user.uuid;
  const invitePending = Boolean(user.invitationOtpValidUntil);
  const inviteProduct = productIdFromUrl(user.invitationFrontendUrl);
  const defaultProduct =
    inviteProduct === "bp" || inviteProduct === "planung" ? inviteProduct : null;
  const fed = user.federatedClient?.[0];
  const twoFactorUuids = (user.twoFactorMethods ?? []).map((m) => m.uuid);

  const actions = accountActions({
    userName: title,
    profileCount,
    emailVerified: Boolean(user.emailVerified),
    invitePending,
    resetPending: Boolean(user.resetPasswordOtpValidUntil),
    newEmail: user.newEmail,
    twoFactorUuids,
    canResendInvitation: Boolean(profileUuid && user.email),
    impersonateOnly: Boolean(user.impersonateOnly),
    hasEmail: Boolean(user.email),
    federated: Boolean(fed),
  });
  const diagnosis = diagnose(diagUserFromAccount(user));

  const setPassword = async () => {
    setBusy(true);
    const res = await updatePassword({ userUuid: user.uuid, newPassword: pw });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Passwort setzen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast(`Neues Passwort für „${title}" gesetzt`);
    setPw("");
    setPwOpen(false);
    onChanged();
  };

  const run = async (action: AccountAction) => {
    if (busy) {
      return;
    }
    if (action.key === "setPassword") {
      setPwOpen(true);
      return;
    }
    if (action.key === "resendInvitation") {
      setInviteOpen(true);
      return;
    }
    if (action.confirm && !(await confirm({ ...action.confirm, cancelText: "Abbrechen" }))) {
      return;
    }
    setBusy(true);
    if (action.remote === "sendPasswordReset") {
      try {
        await sendPasswordResetLink(user.uuid);
        showSuccessToast(action.success ?? "Gesendet");
        onChanged();
      } catch (error) {
        showUserErrorToast({ text: "Reset-Link konnte nicht gesendet werden", error });
      } finally {
        setBusy(false);
      }
      return;
    }
    const isDelete = action.key === "deleteAccount";
    const res = isDelete
      ? await deleteUser({ where: { uuid: user.uuid } })
      : action.revoke === "invitation"
        ? await revokeInvitation({ userUuid: user.uuid })
        : action.revoke === "passwordReset"
          ? await revokePasswordReset({ userUuid: user.uuid })
          : await updateUser({ where: { uuid: user.uuid }, update: action.update });
    if (res.error) {
      setBusy(false);
      showUserErrorToast({
        text: isDelete ? "Löschen fehlgeschlagen" : `${action.label} fehlgeschlagen`,
        error: res.error,
      });
      return;
    }
    if (isDelete) {
      showSuccessToast("Konto gelöscht");
      onDeleted();
      return;
    }
    setBusy(false);
    showSuccessToast(action.success ?? "Gespeichert");
    onChanged();
  };

  /** The row's buttons, stacked in the actions column. */
  const rowActions = (row: AccountRow) => {
    const own = actions.filter((a) => a.row === row);
    if (own.length === 0) {
      return undefined;
    }
    return own.map((a) => (
      <Button
        key={a.key}
        hierarchy="secondary"
        height="s"
        disabled={busy}
        onClick={() => void run(a)}
      >
        {a.rowLabel}
      </Button>
    ));
  };

  const deleteAction = actions.find((a) => a.key === "deleteAccount");

  const emailBadge = user.impersonateOnly ? (
    <StatusBadge status="info" label="Kein eigener Login" />
  ) : user.emailVerified ? (
    <StatusBadge status="ok" label="Bestätigt" />
  ) : (
    <StatusBadge status="err" label="Unbestätigt" />
  );
  const loginKind = user.impersonateOnly
    ? "Kein eigener Login (Elternaccount)"
    : fed
      ? `SSO · ${fed.label || fed.name || "Föderation"}`
      : "Lokal (Passwort)";
  const passwordState = user.passwordChangedAt
    ? `geändert am ${fmtDateTime(user.passwordChangedAt)}`
    : invitePending
      ? "Noch kein Passwort — wird bei der Erstanmeldung vergeben"
      : "Kein Änderungsdatum hinterlegt";
  const BannerIcon = BANNER_ICON[diagnosis.worst];
  const failing = diagnosis.checks.filter((c) => c.status !== "ok");

  return (
    <Card>
      <div className={styles.head}>
        <h2 className={styles.title}>Konto</h2>
        <div className={styles.headActions}>
          <Button hierarchy="secondary" icon={<EditIcon />} onClick={onEdit}>
            Bearbeiten
          </Button>
          {deleteAction && (
            <Button
              hierarchy="secondary"
              icon={<DeleteIcon />}
              disabled={busy}
              onClick={() => void run(deleteAction)}
            >
              {deleteAction.label}
            </Button>
          )}
        </div>
      </div>

      {diagnosis.worst !== "ok" && (
        <div className={`${styles.banner} ${BANNER_CLASS[diagnosis.worst]}`}>
          <span className={styles.bannerIc}>
            <BannerIcon style={{ width: 20, height: 20, color: "#fff" }} />
          </span>
          <div>
            <div className={styles.bannerTitle}>{DIAG_SUMMARY[diagnosis.worst].title}</div>
            <div className={styles.bannerSub}>
              {failing.map((c) => c.label).join(" · ")} — {DIAG_SUMMARY[diagnosis.worst].sub}
            </div>
          </div>
        </div>
      )}

      <div className={styles.rows}>
        <DetailRow grid label="E-Mail (Login)" actions={rowActions("email")}>
          <span className={styles.stacked}>
            <span>{user.email || "—"}</span>
            {emailBadge}
          </span>
        </DetailRow>
        {user.newEmail && (
          <DetailRow grid label="E-Mail-Wechsel" actions={rowActions("emailChange")}>
            {`${user.newEmail} (ausstehend)`}
          </DetailRow>
        )}
        {invitePending && (
          <DetailRow grid label="Einladung" actions={rowActions("invitation")}>
            <PendingLinkValue
              validUntil={user.invitationOtpValidUntil}
              ttlMs={INVITATION_OTP_TTL_MS}
              product={inviteProduct ? productLabel(inviteProduct) : null}
            />
          </DetailRow>
        )}
        <DetailRow grid label="Passwort" actions={rowActions("password")}>
          <span className={styles.stacked}>
            <span>{passwordState}</span>
            {user.resetPasswordOtpValidUntil && (
              <span className={styles.stacked}>
                <span className={styles.muted}>Reset-Link</span>
                <PendingLinkValue
                  validUntil={user.resetPasswordOtpValidUntil}
                  ttlMs={RESET_PASSWORD_OTP_TTL_MS}
                />
              </span>
            )}
          </span>
        </DetailRow>
        <DetailRow grid label="Nur über Elternaccount">
          {user.impersonateOnly ? "Ja" : "Nein"}
        </DetailRow>
        <DetailRow grid label="Anmeldung">
          {loginKind}
        </DetailRow>
        <DetailRow grid label="2FA" actions={rowActions("twoFactor")}>
          <span className={styles.stacked}>
            <span>{twoFactorUuids.length > 0 ? "Aktiv (TOTP)" : "Keine"}</span>
            <span className={styles.muted}>
              {user.recoveryCodesGeneratedAt
                ? `Recovery-Codes erzeugt am ${fmtDateTime(user.recoveryCodesGeneratedAt)}`
                : "Keine Recovery-Codes"}
            </span>
          </span>
        </DetailRow>
        <DetailRow grid label="Sprache">
          {languageLabel(user.preferredLanguage)}
        </DetailRow>
      </div>

      {pwOpen && (
        <Modal
          isOpen
          ariaHideApp={false}
          title={`Passwort setzen — ${title}`}
          onRequestClose={() => setPwOpen(false)}
        >
          <Input
            name="newPassword"
            type="password"
            label="Neues Passwort"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
          />
          <div className={styles.modalActions}>
            <Button hierarchy="tertiary" onClick={() => setPwOpen(false)}>
              Abbrechen
            </Button>
            <Button
              hierarchy="primary"
              disabled={pw.length < 8 || busy}
              onClick={() => void setPassword()}
            >
              Speichern
            </Button>
          </div>
        </Modal>
      )}
      {inviteOpen && profileUuid && user.email && (
        <ResendInvitationModal
          profileUuid={profileUuid}
          email={user.email}
          userName={title}
          products={invitationProducts}
          productUrls={invitationProductUrls}
          defaultProduct={defaultProduct}
          onClose={() => setInviteOpen(false)}
          onSent={() => {
            setInviteOpen(false);
            onChanged();
          }}
        />
      )}
      <ConfirmationDialog />
    </Card>
  );
}
